<script lang="ts">
    import HeadingSmall from '@/components/HeadingSmall.svelte';
    import InputError from '@/components/InputError.svelte';
    import { Button } from '@/components/ui/button';
    import { Input } from '@/components/ui/input';
    import { Label } from '@/components/ui/label';
    import * as Select from '@/components/ui/select';
    import AppLayout from '@/layouts/AppLayout.svelte';
    import SettingsLayout from '@/layouts/settings/Layout.svelte';
    import { type BreadcrumbItem } from '@/types';
    import { useForm } from '@inertiajs/svelte';
    import { fade } from 'svelte/transition';
    import { Trash2 } from 'lucide-svelte';
    import * as Table from '@/components/ui/table';
    import * as AlertDialog from '@/components/ui/alert-dialog';

    interface UserItem {
        id: string;
        name: string;
        email: string;
        roles: string[];
        created_at: string;
    }

    interface Props {
        users: UserItem[];
        availableRoles: string[];
    }

    let { users, availableRoles }: Props = $props();

    const breadcrumbItems: BreadcrumbItem[] = [
        {
            title: 'User Management',
            href: '/settings/users',
        },
    ];

    const form = useForm({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role: availableRoles[0] || 'non-admin',
    });

    const deleteForm = useForm({});

    let userToDelete: UserItem | null = $state(null);
    let showDeleteDialog = $state(false);

    const createUser = (e: Event) => {
        e.preventDefault();
        $form.post(route('users.store'), {
            preserveScroll: true,
            onSuccess: () => {
                $form.reset();
            },
        });
    };

    const confirmDelete = (user: UserItem) => {
        userToDelete = user;
        showDeleteDialog = true;
    };

    const deleteUser = () => {
        if (!userToDelete) return;
        $deleteForm.delete(route('users.destroy', { user: userToDelete.id }), {
            preserveScroll: true,
            onSuccess: () => {
                showDeleteDialog = false;
                userToDelete = null;
            },
        });
    };

    const selectedRoleLabel = $derived(
        availableRoles.find(r => r === $form.role) || 'Select role'
    );
</script>

<svelte:head>
    <title>User Management</title>
</svelte:head>

<AppLayout breadcrumbs={breadcrumbItems}>
    <SettingsLayout>
        <div class="space-y-8">
            <div class="space-y-6">
                <HeadingSmall title="Create New User" description="Add a new user to the system" />

                <form onsubmit={createUser} class="space-y-4">
                    <div class="grid gap-2">
                        <Label for="name">Name</Label>
                        <Input
                            id="name"
                            bind:value={$form.name}
                            type="text"
                            class="mt-1 block w-full"
                            autocomplete="name"
                            placeholder="Full name"
                            required
                        />
                        <InputError message={$form.errors.name} />
                    </div>

                    <div class="grid gap-2">
                        <Label for="email">Email</Label>
                        <Input
                            id="email"
                            bind:value={$form.email}
                            type="email"
                            class="mt-1 block w-full"
                            autocomplete="email"
                            placeholder="Email address"
                            required
                        />
                        <InputError message={$form.errors.email} />
                    </div>

                    <div class="grid gap-2">
                        <Label for="password">Password</Label>
                        <Input
                            id="password"
                            bind:value={$form.password}
                            type="password"
                            class="mt-1 block w-full"
                            autocomplete="new-password"
                            placeholder="Password"
                            required
                        />
                        <InputError message={$form.errors.password} />
                    </div>

                    <div class="grid gap-2">
                        <Label for="password_confirmation">Confirm Password</Label>
                        <Input
                            id="password_confirmation"
                            bind:value={$form.password_confirmation}
                            type="password"
                            class="mt-1 block w-full"
                            autocomplete="new-password"
                            placeholder="Confirm password"
                            required
                        />
                        <InputError message={$form.errors.password_confirmation} />
                    </div>

                    <div class="grid gap-2">
                        <Label for="role">Role</Label>
                        <Select.Root type="single" bind:value={$form.role}>
                            <Select.Trigger class="w-full">
                                {selectedRoleLabel}
                            </Select.Trigger>
                            <Select.Content>
                                {#each availableRoles as role}
                                    <Select.Item value={role}>{role}</Select.Item>
                                {/each}
                            </Select.Content>
                        </Select.Root>
                        <InputError message={$form.errors.role} />
                    </div>

                    <div class="flex items-center gap-4">
                        <Button type="submit" disabled={$form.processing}>
                            {#if $form.processing}Creating...{:else}Create User{/if}
                        </Button>

                        {#if $form.recentlySuccessful}
                            <p class="text-sm text-green-600" transition:fade={{ duration: 150 }}>User created.</p>
                        {/if}
                    </div>
                </form>
            </div>

            <div class="space-y-4">
                <HeadingSmall title="Existing Users" description="Manage users in the system" />

                <Table.Root>
                    <Table.Header>
                        <Table.Row>
                            <Table.Head>Name</Table.Head>
                            <Table.Head>Email</Table.Head>
                            <Table.Head>Roles</Table.Head>
                            <Table.Head class="w-[100px]">Actions</Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {#each users as user (user.id)}
                            <Table.Row>
                                <Table.Cell class="font-medium">{user.name}</Table.Cell>
                                <Table.Cell>{user.email}</Table.Cell>
                                <Table.Cell>
                                    {#each user.roles as role, i}
                                        <span class="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                                            {role}
                                        </span>
                                        {#if i < user.roles.length - 1}
                                            <span class="mx-1"></span>
                                        {/if}
                                    {/each}
                                </Table.Cell>
                                <Table.Cell>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onclick={() => confirmDelete(user)}
                                        class="text-destructive hover:text-destructive"
                                    >
                                        <Trash2 class="h-4 w-4" />
                                    </Button>
                                </Table.Cell>
                            </Table.Row>
                        {/each}
                    </Table.Body>
                </Table.Root>
            </div>
        </div>

        <AlertDialog.Root bind:open={showDeleteDialog}>
            <AlertDialog.Content>
                <AlertDialog.Header>
                    <AlertDialog.Title>Delete User</AlertDialog.Title>
                    <AlertDialog.Description>
                        Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
                    </AlertDialog.Description>
                </AlertDialog.Header>
                <AlertDialog.Footer>
                    <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                    <AlertDialog.Action onclick={deleteUser} class="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Delete
                    </AlertDialog.Action>
                </AlertDialog.Footer>
            </AlertDialog.Content>
        </AlertDialog.Root>
    </SettingsLayout>
</AppLayout>